/* announce() — the single live-region announcer (strategy §14.3).
   For programmatic AT announcements where no live DOM exists: async ops
   completing, AI status changes, background saves. NOT used by Toast —
   toasts carry their own role="status"/"alert" live semantics; calling
   announce() for them too would double-announce.

   One visually-hidden region per politeness level, created lazily and
   reused. Re-announcing the same string works (content is cleared first;
   the swap is what AT reacts to). */

const REGION_ATTR = "data-pbds-live-region";

function getRegion(politeness: "polite" | "assertive"): HTMLElement {
  let region = document.querySelector<HTMLElement>(
    `[${REGION_ATTR}="${politeness}"]`,
  );
  if (!region) {
    region = document.createElement("div");
    region.setAttribute(REGION_ATTR, politeness);
    region.setAttribute("aria-live", politeness);
    region.setAttribute("aria-atomic", "true");
    region.setAttribute(
      "role",
      politeness === "assertive" ? "alert" : "status",
    );
    // Visually hidden, but available to AT.
    Object.assign(region.style, {
      position: "absolute",
      width: "1px",
      height: "1px",
      margin: "-1px",
      padding: "0",
      border: "0",
      clip: "rect(0 0 0 0)",
      clipPath: "inset(50%)",
      overflow: "hidden",
      whiteSpace: "nowrap",
    });
    document.body.appendChild(region);
  }
  return region;
}

export interface AnnounceOptions {
  politeness?: "polite" | "assertive";
}

/** Announce a message to assistive technology. */
export function announce(
  message: string,
  { politeness = "polite" }: AnnounceOptions = {},
): void {
  if (typeof document === "undefined") return;
  const region = getRegion(politeness);
  // Clear-then-set so repeating the same message re-announces.
  region.textContent = "";
  window.setTimeout(() => {
    region.textContent = message;
  }, 0);
}

/** Test hook — removes regions so specs start clean. Not for runtime use. */
export function __resetAnnouncer(): void {
  for (const el of document.querySelectorAll(`[${REGION_ATTR}]`)) el.remove();
}
